/**
 * Body UI for question_type === "health_condition".
 * Renders inside QuestionFrame like other questions (same card chrome).
 * Blue info card + dual path buttons; selecting advances (no Continue).
 */

import React from "react";
import { orderAnswerOptionsChildren } from "../components/helpers";
import { isHealthConditionNothingChanged } from "./navigationHelpers";
import { useShellIdFn } from "./shellContext";
import * as translator from "../../../utils/translator";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

const ORANGE_GRADIENT = "linear-gradient(90deg, #f59200 0%, #ffbb29 100%)";
const ORANGE = "#f08e00";

interface HealthConditionAnswersProps {
  answersGroup: any;
  answersSelectedMap: Record<string | number, any>;
  onSelect: (child: any) => void;
  errorMode?: boolean;
  language?: string;
  /** Blue info card — question.help_block from CMS. */
  infoHtml?: string;
}

const InfoIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden className="shrink-0">
    <circle cx="8" cy="8" r="6.5" stroke="#1F638C" strokeWidth="1.25" />
    <path d="M8 7.15v3.4" stroke="#1F638C" strokeWidth="1.25" strokeLinecap="round" />
    <circle cx="8" cy="5.15" r="0.9" fill="#1F638C" />
  </svg>
);

/** First line only when CMS stuffed a subtitle into the same value string. */
function primaryLabelHtml(value: any): string {
  const raw = String(value ?? "");
  const diamondIdx = raw.search(/[✦✧]/);
  if (diamondIdx > 0) return raw.slice(0, diamondIdx).trim();
  const nlIdx = raw.search(/[\n\r]/);
  if (nlIdx > 0) return raw.slice(0, nlIdx).trim();
  const tails = [
    "full questionnaire",
    "vollständiger fragebogen",
    "questionnaire complet",
    "skip 10",
    "fragen überspringen",
    "passer 10",
  ];
  const lower = raw.toLowerCase();
  for (const tail of tails) {
    const idx = lower.indexOf(tail);
    if (idx > 0) return raw.slice(0, idx).trim();
  }
  return raw;
}

const HealthConditionAnswers: React.FC<HealthConditionAnswersProps> = ({
  answersGroup,
  answersSelectedMap,
  onSelect,
  errorMode,
  language,
  infoHtml,
}) => {
  // Both shells render this list, so scope the ids to keep them unique.
  const scopeId = useShellIdFn();

  const children = orderAnswerOptionsChildren(
    answersGroup?.children || [],
    answersGroup?.recipe?.recipe_question_id
  );

  // Primary path ("nothing changed") always on top.
  const ordered = [...children].sort((a, b) => {
    const aPrimary = isHealthConditionNothingChanged(a.recipe.value) ? 0 : 1;
    const bPrimary = isHealthConditionNothingChanged(b.recipe.value) ? 0 : 1;
    return aPrimary - bPrimary;
  });

  return (
    <div className="flex flex-col w-full gap-6">
      {infoHtml ? (
        // A long CMS condition list must not push the answer buttons off the
        // fold — the card itself scrolls instead of growing without bound, so
        // the Yes/No choices stay reachable on short viewports.
        <div id={scopeId("wl2_health_condition_info")} className="w-full rounded-[10px] bg-[#edf9fc] px-4 py-[14px] flex flex-col gap-1 items-start box-border">
          <InfoIcon />
          <div
            className="font-['Roboto'] font-normal text-[12px] leading-[1.4] text-[#1f638c] w-full overflow-y-auto overscroll-contain max-h-[38dvh] md:max-h-[32dvh] [&_a]:underline [&_p]:m-0 [&_p]:leading-[1.4]"
            dangerouslySetInnerHTML={{ __html: infoHtml }}
          />
        </div>
      ) : null}

      <div id={scopeId("wl2_health_condition_answers")} className="flex flex-col gap-3 w-full">
        {ordered.map((child: any) => {
          const selected = !!answersSelectedMap[child.recipe.id];
          const invalid = !!errorMode && !selected;
          const primary = isHealthConditionNothingChanged(child.recipe.value);
          const subtitle = primary
            ? t(language, "health_condition_skip_subtitle")
            : t(language, "health_condition_full_subtitle");
          const label = primaryLabelHtml(child.recipe.value);

          return (
            <button
              key={child.recipe.id}
              id={scopeId(`answer_${child.recipe.id}`)}
              type="button"
              onClick={() => onSelect(child)}
              className={`w-full box-border rounded-[16px] px-6 flex flex-col items-start justify-center gap-1 text-left cursor-pointer transition-transform duration-100 active:scale-[0.98] hover:shadow-md ${
                primary
                  ? "min-h-[72px] py-5 border-0 text-white hover:opacity-95"
                  : "min-h-[64px] py-4 bg-white hover:bg-[#fff8ee]"
              }`}
              style={
                primary
                  ? {
                      backgroundImage: ORANGE_GRADIENT,
                      boxShadow: selected ? "0 0 0 2px rgba(245,146,0,0.35)" : undefined,
                    }
                  : {
                      border: `1px solid ${invalid ? "var(--primary-danger)" : ORANGE}`,
                      backgroundColor: selected ? "#fff7ec" : "#ffffff",
                    }
              }
            >
              <span
                className={`font-['Poppins'] font-medium text-[16px] leading-5 w-full ${
                  primary ? "text-white" : invalid ? "text-[var(--primary-danger)]" : "text-[#08080a]"
                }`}
                dangerouslySetInnerHTML={{ __html: label }}
              />
              {subtitle ? (
                <span
                  className={`font-['Roboto'] font-normal text-[13px] leading-[18px] w-full ${
                    primary ? "text-white/95" : "text-[#99a3b2]"
                  }`}
                >
                  {subtitle}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default HealthConditionAnswers;
